import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CopyX, Loader2 } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export function DuplicateMvrPanel({ customerId, onRemoved }) {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const find = async () => {
    setBusy(true);
    try { const { data } = await api.get("/drivers/bulk-mvr/duplicates", { params: customerId ? { customer_id: customerId } : {} }); setScan(data); if (!data.count) toast.success("No duplicate annual MVRs found"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Remove ${scan.count} duplicate MVR(s)? The earliest upload for each driver + date is kept; the extras, their attachments, AI drafts, pending review sheets and ${money(scan.refund)} of billing lines are deleted.`)) return;
    setBusy(true);
    try { const { data } = await api.post("/drivers/bulk-mvr/duplicates/remove", { customer_id: customerId || null }); toast.success(`${data.removed} duplicate MVR(s) removed${data.refund ? ` · ${money(data.refund)} taken off billing` : ""}`); setScan(null); onRemoved?.(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-slate-200 p-3 text-xs" data-testid="dup-mvr-panel">
      <div className="flex flex-wrap items-center gap-2">
        <CopyX className="h-4 w-4 text-slate-500" /><span className="font-medium text-slate-800">Duplicate annual MVRs</span>
        <span className="text-slate-500">same driver + same pulled-on date{customerId ? "" : " · all customers"}</span>
        <Button size="sm" variant="outline" className="h-7 ml-auto" disabled={busy} onClick={find} data-testid="dup-mvr-scan">{busy && !scan ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Scan for duplicates"}</Button>
      </div>
      {scan && scan.count > 0 && (
        <div className="mt-2 space-y-2" data-testid="dup-mvr-results">
          <div className="rounded bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-amber-900"><b>{scan.count}</b> duplicate upload{scan.count === 1 ? "" : "s"} across {scan.groups.length} driver-date{scan.groups.length === 1 ? "" : "s"}{scan.refund ? ` · ${money(scan.refund)} currently double-billed` : ""}</div>
          <ul className="max-h-48 overflow-y-auto divide-y divide-slate-100">
            {scan.groups.map((g) => (
              <li key={`${g.driver_id}-${g.pulled_on}`} className="py-1.5" data-testid={`dup-mvr-group-${g.driver_id}-${g.pulled_on}`}>
                <div><b>{g.driver}</b> · pulled {g.pulled_on}</div>
                <div className="text-emerald-700">keep · {g.keep.files.join(", ") || "(no file)"} · {(g.keep.created_at || "").slice(0, 16).replace("T", " ")}</div>
                {g.remove.map((r) => <div key={r.id} className="text-rose-700 line-through">remove · {r.files.join(", ") || "(no file)"} · {(r.created_at || "").slice(0, 16).replace("T", " ")}{r.total ? ` · ${money(r.total)}` : ""}</div>)}
              </li>
            ))}
          </ul>
          <div className="flex justify-end"><Button size="sm" className="h-7 bg-rose-600 hover:bg-rose-700" disabled={busy} onClick={remove} data-testid="dup-mvr-remove">Remove {scan.count} duplicate{scan.count === 1 ? "" : "s"}</Button></div>
        </div>
      )}
    </div>
  );
}
